package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextDecoration
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebPortraitLayout
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.logic.MerchantLoginViewModel

@Composable
fun MerchantLoginView(viewModel: MerchantLoginViewModel) {
    if (PlatformHelper.isWeb && !rememberIsWebPortraitLayout()) {
        MerchantLoginViewWeb(viewModel)
        return
    }
    val keyboardController = LocalSoftwareKeyboardController.current
    val theme = AppTheme.current

    Box(modifier = Modifier.fillMaxSize()) {
        BottomSheetUIView(
            viewModel = viewModel,
            bottomSheetFactory = {}
        ) { uiState ->
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .padding(horizontal = p16),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // Sign in – Figma: 380×48, Display Small SemiBold 36, On Surface, left-aligned (extra top padding to avoid overlap with nav bar)
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p32, bottom = p24),
                    contentAlignment = Alignment.CenterStart
                ) {
                    Texts.DisplaySmall(
                        text = stringResource(SR.strings.login_as_merchant),
                        color = theme.colors.onSurface,
                    )
                }

                // Email input – Figma: 380×56, cornerRadius 12, padding 16/18
                DefaultCircleShapedTextField(
                    modifier = Modifier
                        .fillMaxWidth(),
                    value = uiState.value.email,
                    label = stringResource(SR.strings.email_address),
                    errorText = uiState.value.emailErrorMsg,
                    onValueChange = viewModel::onEmailChange,
                    isError = uiState.value.emailIsError,
                    inputType = TextFieldType.EMAIL,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Email,
                        imeAction = ImeAction.Next
                    )
                )

                // Password input – Figma: 380×56, cornerRadius 12, eye 24×24
                DefaultCircleShapedTextField(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p24),
                    value = uiState.value.password,
                    label = stringResource(SR.strings.password),
                    errorText = uiState.value.passwordErrorMsg,
                    errorLeadingIcon = SRC.images.password_error_icon,
                    onValueChange = viewModel::onPasswordChange,
                    isError = uiState.value.passwordIsError,
                    visualTransformation = if (uiState.value.passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    trailingIcon = {
                        Image(
                            painter = painterResource(
                                if (uiState.value.passwordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                            ),
                            modifier = Modifier
                                .size(p24)
                                .noRippleClickable { viewModel.togglePasswordVisibility() }
                        )
                    },
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Password,
                        imeAction = ImeAction.Done
                    )
                )

                // Forgot password? – Figma: 380×20, right-aligned, Primary Variant (green), underlined
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p24),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = stringResource(SR.strings.forgot_password_question),
                        color = theme.colors.primary,
                        style = theme.typography.bodyMedium,
                        textDecoration = TextDecoration.Underline,
                        modifier = Modifier.noRippleClickable { viewModel.onForgotPasswordClicked() }
                    )
                }

                Spacer(Modifier.weight(1f))

                if (uiState.value.showBiometricLogin) {
                    val biometricCaption = when (uiState.value.biometricType) {
                        BiometricAuthenticationType.FACE -> stringResource(SR.strings.login_with_faceID)
                        BiometricAuthenticationType.FINGERPRINT -> stringResource(SR.strings.login_with_fingerprint)
                        else -> ""
                    }

                    if (biometricCaption.isNotEmpty()) {
                        DefaultButton(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(bottom = p8),
                            caption = biometricCaption,
                            type = DefaultButtonType.OUTLINED,
                            callback = {
                                keyboardController?.hide()
                                viewModel.onBiometricLoginClicked()
                            },
                            identifier = "Merchant Biometric Login"
                        )
                    }
                }

                // Log In button – Figma: 380×56, cornerRadius 100, padding 10
                DefaultButton(
                    modifier = Modifier.fillMaxWidth(),
                    caption = stringResource(SR.strings.sign_in),
                    callback = {
                        keyboardController?.hide()
                        viewModel.onLoginClicked()
                    },
                    identifier = "Merchant Login"
                )

                // Don't have an account? Create an account – Figma: 380×20, center, grey + green link
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p16, bottom = p8),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Texts.BodyMedium(
                        text = stringResource(SR.strings.dont_have_account)+" ",
                        color = theme.colors.onSurfaceVariant
                    )
                    Text(
                        text = stringResource(SR.strings.continue_as_merchant),
                        color = theme.colors.primary,
                        style = theme.typography.bodyMedium,
                        textDecoration = TextDecoration.Underline,
                        modifier = Modifier.noRippleClickable { viewModel.onCreateAccountClicked() }
                    )
                }
            }
        }
    }
}

